import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import SummaryCard from '../components/SummaryCard';
import TaskTable from '../components/TaskTable';
import StatusChart from '../components/Charts/StatusChart';
import OnTimeVsDelayedChart from '../components/Charts/OnTimeVsDelayedChart';
import MemberPerformanceChart from '../components/Charts/MemberPerformanceChart';
import EstimatedVsActualChart from '../components/Charts/EstimatedVsActualChart';
import { AdminDashboardSkeleton } from '../components/DashboardSkeleton';
import { useToast } from '../context/ToastContext';
import { getTasks, getUsers, updateTask, deleteTask, isTaskOnTime, TASK_STATUSES } from '../data/supabaseApi';
import { FiClipboard, FiCheckCircle, FiAlertCircle, FiTrendingUp } from 'react-icons/fi';
import './AdminDashboard.css';

const DOWNLOAD_COLUMNS = [
  { label: 'Task', value: (t) => t.taskName || t.task || '' },
  { label: 'Client', value: (t) => t.clientName || '' },
  { label: 'Project', value: (t) => t.projectName || t.project || '' },
  { label: 'Member', value: (t) => t.assignedMember || t.userName || '' },
  { label: 'Planned Start', value: (t) => t.plannedStartDate || '' },
  { label: 'Planned End', value: (t) => t.plannedEndDate || '' },
  { label: 'Status', value: (t) => t.currentStatus || t.status || '' },
  { label: 'Estimated Hours', value: (t) => (t.estimatedTime ?? '') },
  { label: 'Actual Hours', value: (t) => (t.actualTime ?? '') },
  { label: 'Completion Date', value: (t) => t.completionDate || '' },
  { label: 'On Time', value: (t) => {
    const status = t.currentStatus || t.status;
    if (status !== 'Completed') return '';
    const onTime = isTaskOnTime(t);
    if (onTime === true) return 'Yes';
    if (onTime === false) return 'No';
    return '';
  } },
  { label: 'Delay Reason', value: (t) => t.delayReason || '' },
  { label: 'Admin Remarks', value: (t) => t.adminRemarks || '' },
  { label: 'Admin Verdict', value: (t) => t.adminVerdict || '' }
];

const toCsvCell = (value) => {
  const str = value == null ? '' : String(value);
  return `"${str.replace(/"/g, '""')}"`;
};

const getMemberName = (task) => (task.assignedMember || task.userName || '').trim();

const sortTasksByMemberName = (list) =>
  [...list].sort((a, b) => {
    const memberCompare = getMemberName(a).localeCompare(getMemberName(b), undefined, { sensitivity: 'base' });
    if (memberCompare !== 0) return memberCompare;
    return (a.taskName || a.task || '').localeCompare((b.taskName || b.task || ''), undefined, { sensitivity: 'base' });
  });

const escapePdfText = (value) =>
  String(value == null ? '' : value)
    .replace(/\\/g, '\\\\')
    .replace(/\(/g, '\\(')
    .replace(/\)/g, '\\)');

const wrapText = (text, maxLen) => {
  if (!text) return [''];
  const words = String(text).split(/\s+/);
  const lines = [];
  let current = '';
  words.forEach((word) => {
    const candidate = current ? `${current} ${word}` : word;
    if (candidate.length <= maxLen) {
      current = candidate;
      return;
    }
    if (current) lines.push(current);
    current = word;
  });
  if (current) lines.push(current);
  return lines.length ? lines : [''];
};

const PDF_COLUMNS = [
  { key: 'index', title: '#', x: 32, width: 22, maxChars: 4 },
  { key: 'member', title: 'Member', x: 54, width: 78, maxChars: 16 },
  { key: 'task', title: 'Task', x: 132, width: 150, maxChars: 33 },
  { key: 'project', title: 'Project', x: 282, width: 100, maxChars: 21 },
  { key: 'status', title: 'Status', x: 382, width: 60, maxChars: 12 },
  { key: 'start', title: 'Start', x: 442, width: 45, maxChars: 10 },
  { key: 'end', title: 'End', x: 487, width: 45, maxChars: 10 },
  { key: 'done', title: 'Done', x: 532, width: 45, maxChars: 10 }
];

const LINE_HEIGHT = 11;
const ROW_PADDING = 4;
const TABLE_HEADER_HEIGHT = 18;
const TABLE_TOP_Y = 700;
const TABLE_BOTTOM_Y = 58;

const buildPdfRows = (tasks) =>
  tasks.map((task, idx) => {
    const row = {
      index: String(idx + 1),
      member: getMemberName(task),
      task: task.taskName || task.task || '-',
      project: task.projectName || task.project || '-',
      status: task.currentStatus || task.status || '-',
      start: task.plannedStartDate || '-',
      end: task.plannedEndDate || '-',
      done: task.completionDate || '-'
    };
    const wrapped = {};
    let maxLines = 1;
    PDF_COLUMNS.forEach((col) => {
      const lines = wrapText(row[col.key], col.maxChars);
      wrapped[col.key] = lines;
      if (lines.length > maxLines) maxLines = lines.length;
    });
    return {
      wrapped,
      height: maxLines * LINE_HEIGHT + ROW_PADDING
    };
  });

const paginatePdfRows = (rows) => {
  const pages = [];
  let current = [];
  let y = TABLE_TOP_Y - TABLE_HEADER_HEIGHT;
  rows.forEach((row) => {
    if (y - row.height < TABLE_BOTTOM_Y) {
      pages.push(current);
      current = [];
      y = TABLE_TOP_Y - TABLE_HEADER_HEIGHT;
    }
    current.push(row);
    y -= row.height;
  });
  if (current.length) pages.push(current);
  return pages.length ? pages : [[]];
};

const createPageStream = (pageRows, pageNumber, pageCount, meta) => {
  const commands = [];
  commands.push('BT');
  commands.push('/F1 14 Tf');
  commands.push(`36 768 Td (${escapePdfText('Admin Progress Report')}) Tj`);
  commands.push('/F1 9 Tf');
  commands.push(`0 -16 Td (${escapePdfText(meta.dateRange)}) Tj`);
  commands.push(`0 -12 Td (${escapePdfText(meta.taskCount)}) Tj`);
  commands.push(`0 -12 Td (${escapePdfText('Sorted by member name')}) Tj`);
  commands.push(`420 0 Td (${escapePdfText(`Page ${pageNumber} of ${pageCount}`)}) Tj`);
  commands.push('ET');

  const tableLeft = PDF_COLUMNS[0].x;
  const tableRight = PDF_COLUMNS[PDF_COLUMNS.length - 1].x + PDF_COLUMNS[PDF_COLUMNS.length - 1].width;
  let y = TABLE_TOP_Y;

  commands.push('0.4 w');
  commands.push(`${tableLeft} ${y} m ${tableRight} ${y} l S`);
  commands.push(`${tableLeft} ${y - TABLE_HEADER_HEIGHT} m ${tableRight} ${y - TABLE_HEADER_HEIGHT} l S`);
  let xCursor = tableLeft;
  PDF_COLUMNS.forEach((col) => {
    commands.push(`${xCursor} ${y} m ${xCursor} ${TABLE_BOTTOM_Y} l S`);
    xCursor += col.width;
  });
  commands.push(`${tableRight} ${y} m ${tableRight} ${TABLE_BOTTOM_Y} l S`);

  commands.push('BT');
  commands.push('/F1 8 Tf');
  PDF_COLUMNS.forEach((col) => {
    commands.push(`1 0 0 1 ${col.x + 2} ${y - 12} Tm (${escapePdfText(col.title)}) Tj`);
  });
  commands.push('ET');

  y -= TABLE_HEADER_HEIGHT;

  pageRows.forEach((row) => {
    const rowTop = y;
    const rowBottom = rowTop - row.height;
    commands.push(`${tableLeft} ${rowBottom} m ${tableRight} ${rowBottom} l S`);
    commands.push('BT');
    commands.push('/F1 8 Tf');
    PDF_COLUMNS.forEach((col) => {
      const lines = row.wrapped[col.key];
      lines.forEach((line, idx) => {
        const textY = rowTop - 10 - idx * LINE_HEIGHT;
        commands.push(`1 0 0 1 ${col.x + 2} ${textY} Tm (${escapePdfText(line)}) Tj`);
      });
    });
    commands.push('ET');
    y = rowBottom;
  });

  return commands.join('\n');
};

const createPdfBlobFromRows = (pages, meta) => {
  const objects = {};
  const pageCount = pages.length || 1;
  const pageObjectIds = [];

  objects[1] = '<< /Type /Catalog /Pages 2 0 R >>';
  objects[3] = '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>';

  pages.forEach((pageRows, idx) => {
    const pageObjectId = 4 + idx * 2;
    const contentObjectId = pageObjectId + 1;
    pageObjectIds.push(pageObjectId);
    const stream = createPageStream(pageRows, idx + 1, pageCount, meta);

    objects[pageObjectId] = `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 612 792] /Resources << /Font << /F1 3 0 R >> >> /Contents ${contentObjectId} 0 R >>`;
    objects[contentObjectId] = `<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`;
  });

  objects[2] = `<< /Type /Pages /Kids [${pageObjectIds.map((id) => `${id} 0 R`).join(' ')}] /Count ${pageCount} >>`;
  const maxObjectId = 3 + pageCount * 2;

  let pdf = '%PDF-1.4\n';
  const xref = [0];
  for (let objId = 1; objId <= maxObjectId; objId += 1) {
    const obj = objects[objId];
    xref.push(pdf.length);
    pdf += `${objId} 0 obj\n${obj}\nendobj\n`;
  }
  const xrefStart = pdf.length;
  pdf += `xref\n0 ${maxObjectId + 1}\n`;
  pdf += '0000000000 65535 f \n';
  xref.slice(1).forEach((offset) => {
    pdf += `${String(offset).padStart(10, '0')} 00000 n \n`;
  });
  pdf += `trailer\n<< /Size ${maxObjectId + 1} /Root 1 0 R >>\nstartxref\n${xrefStart}\n%%EOF`;

  return new Blob([pdf], { type: 'application/pdf' });
};

const AdminDashboard = () => {
  const { addToast } = useToast();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    member: '',
    client: '',
    project: '',
    status: '',
    onTime: ''
  });
  const [dateRange, setDateRange] = useState({ from: '', to: '' });

  const refreshTasks = async () => {
    try {
      const list = await getTasks();
      setTasks(list);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    let cancelled = false;
    async function init() {
      try {
        const [tasksList, usersList] = await Promise.all([getTasks(), getUsers()]);
        if (cancelled) return;
        setTasks(tasksList);
        setUsers(usersList);
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    init();
    return () => { cancelled = true; };
  }, []);


  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({ ...prev, [filterType]: value }));
  };

  const getFilteredTasks = () => {
    let filtered = tasks;
    const memberName = (t) => t.assignedMember || t.userName;

    if (selectedMember) filtered = filtered.filter(t => memberName(t) === selectedMember);
    if (filters.member) filtered = filtered.filter(t => memberName(t) === filters.member);
    if (filters.client) filtered = filtered.filter(t => t.clientName === filters.client);
    if (filters.project) filtered = filtered.filter(t => (t.projectName || t.project) === filters.project);
    if (filters.status) filtered = filtered.filter(t => (t.currentStatus || t.status) === filters.status);
    if (filters.onTime === 'on-time') {
      filtered = filtered.filter(t => (t.currentStatus || t.status) === 'Completed' && isTaskOnTime(t) === true);
    } else if (filters.onTime === 'delayed') {
      filtered = filtered.filter(t => ((t.currentStatus || t.status) === 'Delayed') || ((t.currentStatus || t.status) === 'Completed' && isTaskOnTime(t) === false));
    }
    if (dateRange.from) {
      filtered = filtered.filter((t) => {
        const d = t.plannedStartDate || t.plannedEndDate || t.completionDate;
        return d ? d >= dateRange.from : false;
      });
    }
    if (dateRange.to) {
      filtered = filtered.filter((t) => {
        const d = t.plannedStartDate || t.plannedEndDate || t.completionDate;
        return d ? d <= dateRange.to : false;
      });
    }
    return sortTasksByMemberName(filtered);
  };

  const getSummaryStats = (taskList) => {
    const total = taskList.length;
    const completed = taskList.filter(t => (t.currentStatus || t.status) === 'Completed').length;
    let onTime = 0;
    let delayed = 0;
    taskList.forEach(t => {
      const s = t.currentStatus || t.status;
      if (s === 'Completed') {
        if (isTaskOnTime(t) === true) onTime++;
        else delayed++;
      } else if (s === 'Delayed') delayed++;
    });
    const completionRate = total ? ((completed / total) * 100).toFixed(1) : '0';
    return { total, onTime, delayed, completionRate: completionRate + '%' };
  };

  const getMemberPerformanceRows = (taskList, members) => {
    return members.map((name) => {
      const memberTasks = taskList.filter((t) => getMemberName(t) === name);
      const total = memberTasks.length;
      const completed = memberTasks.filter((t) => (t.currentStatus || t.status) === 'Completed').length;
      const onTime = memberTasks.filter((t) => (t.currentStatus || t.status) === 'Completed' && isTaskOnTime(t) === true).length;
      const delayed = memberTasks.filter((t) => {
        const s = t.currentStatus || t.status;
        return s === 'Delayed' || (s === 'Completed' && isTaskOnTime(t) === false);
      }).length;
      const inProgress = memberTasks.filter((t) => {
        const s = t.currentStatus || t.status;
        return !['Completed', 'Delayed', 'Blocked'].includes(s);
      }).length;
      const completionRate = total ? `${((completed / total) * 100).toFixed(1)}%` : '0.0%';
      return { name, total, completed, onTime, delayed, inProgress, completionRate };
    });
  };

  const getUniqueClients = () => [...new Set(tasks.map(t => t.clientName).filter(Boolean))].sort();
  const getUniqueProjects = () => [...new Set(tasks.map(t => t.projectName || t.project).filter(Boolean))].sort();

  const handleAdminUpdate = async (taskId, data) => {
    try {
      await updateTask(taskId, data);
      await refreshTasks();
      addToast('Changes saved.', 'success');
    } catch (e) {
      console.error(e);
      addToast('Failed to save. Please try again.', 'error');
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await deleteTask(taskId);
      await refreshTasks();
      addToast('Task deleted.', 'success');
    } catch (e) {
      console.error(e);
      addToast('Failed to delete task. Please try again.', 'error');
    }
  };

  const downloadCsv = () => {
    if (filteredTasks.length === 0) {
      addToast('No tasks to export for current filters.', 'error');
      return;
    }
    const header = DOWNLOAD_COLUMNS.map(c => toCsvCell(c.label)).join(',');
    const rows = filteredTasks.map((task) => DOWNLOAD_COLUMNS.map(c => toCsvCell(c.value(task))).join(','));
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const suffixFrom = dateRange.from || 'all';
    const suffixTo = dateRange.to || 'all';
    a.href = url;
    a.download = `task-report-${suffixFrom}-to-${suffixTo}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    addToast('CSV downloaded.', 'success');
  };

  const downloadPdf = () => {
    if (filteredTasks.length === 0) {
      addToast('No tasks to export for current filters.', 'error');
      return;
    }
    const fromText = dateRange.from || 'All';
    const toText = dateRange.to || 'All';
    const rows = buildPdfRows(filteredTasks);
    const pages = paginatePdfRows(rows);
    const blob = createPdfBlobFromRows(pages, {
      dateRange: `Date range: ${fromText} to ${toText}`,
      taskCount: `Total tasks: ${filteredTasks.length}`
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const suffixFrom = dateRange.from || 'all';
    const suffixTo = dateRange.to || 'all';
    a.href = url;
    a.download = `task-report-${suffixFrom}-to-${suffixTo}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    addToast('PDF downloaded.', 'success');
  };

  const filteredTasks = getFilteredTasks();
  const stats = getSummaryStats(filteredTasks);
  const memberNames = users
    .filter(u => u.role === 'member')
    .map(u => u.name)
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  const clients = getUniqueClients();
  const projects = getUniqueProjects();
  const memberPerformanceRows = getMemberPerformanceRows(filteredTasks, memberNames);
  const hasActiveFilters = filters.member || filters.client || filters.project || filters.status || filters.onTime || dateRange.from || dateRange.to;

  if (loading) {
    return <AdminDashboardSkeleton />;
  }

  return (
    <div className="dashboard-layout">
      <Sidebar
        userRole="admin"
        selectedMember={selectedMember}
        onMemberSelect={setSelectedMember}
      />
      <div className="dashboard-content">
        <Topbar
          userRole="admin"
        />
        <div className="dashboard-main">
          {selectedMember && (
            <div className="member-header">
              <h2>{selectedMember}'s performance</h2>
              <button type="button" className="back-to-all-btn" onClick={() => setSelectedMember(null)}>
                ← Back to all members
              </button>
            </div>
          )}

          <div className="summary-cards">
            <SummaryCard title="Total tasks" value={stats.total} icon={FiClipboard} color="#475569" />
            <SummaryCard title="On-time" value={stats.onTime} icon={FiCheckCircle} color="#22c55e" />
            <SummaryCard title="Delayed" value={stats.delayed} icon={FiAlertCircle} color="#ef4444" />
            <SummaryCard title="Completion rate" value={stats.completionRate} icon={FiTrendingUp} color="#3b82f6" />
          </div>

          <div className="member-performance-section">
            <h2>Member Performance Overview</h2>
            <div className="member-performance-table-wrap">
              <table className="member-performance-table">
                <thead>
                  <tr>
                    <th>Member</th>
                    <th>Total</th>
                    <th>Completed</th>
                    <th>On-time</th>
                    <th>Delayed</th>
                    <th>In progress</th>
                    <th>Completion %</th>
                  </tr>
                </thead>
                <tbody>
                  {memberPerformanceRows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="member-performance-empty">No member data found</td>
                    </tr>
                  ) : (
                    memberPerformanceRows.map((row) => (
                      <tr key={row.name}>
                        <td>{row.name}</td>
                        <td>{row.total}</td>
                        <td>{row.completed}</td>
                        <td>{row.onTime}</td>
                        <td>{row.delayed}</td>
                        <td>{row.inProgress}</td>
                        <td>{row.completionRate}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="tasks-section">
            <div className="section-header">
              <h2>All tasks</h2>
              <div className="filters">
                <select
                  className="filter-select"
                  value={filters.member}
                  onChange={(e) => handleFilterChange('member', e.target.value)}
                >
                  <option value="">All members</option>
                  {memberNames.map(name => <option key={name} value={name}>{name}</option>)}
                </select>
                <select
                  className="filter-select"
                  value={filters.client}
                  onChange={(e) => handleFilterChange('client', e.target.value)}
                >
                  <option value="">All clients</option>
                  {clients.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <select
                  className="filter-select"
                  value={filters.project}
                  onChange={(e) => handleFilterChange('project', e.target.value)}
                >
                  <option value="">All projects</option>
                  {projects.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
                <select
                  className="filter-select"
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <option value="">All statuses</option>
                  {TASK_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <select
                  className="filter-select"
                  value={filters.onTime}
                  onChange={(e) => handleFilterChange('onTime', e.target.value)}
                >
                  <option value="">On-time / Delayed</option>
                  <option value="on-time">On-time only</option>
                  <option value="delayed">Delayed only</option>
                </select>
                <input
                  type="date"
                  className="filter-input"
                  value={dateRange.from}
                  onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
                  aria-label="From date"
                />
                <input
                  type="date"
                  className="filter-input"
                  value={dateRange.to}
                  onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
                  aria-label="To date"
                />
                <button type="button" className="export-btn" onClick={downloadCsv}>
                  Download Excel (CSV)
                </button>
                <button type="button" className="export-btn export-btn-pdf" onClick={downloadPdf}>
                  Download PDF
                </button>
                {hasActiveFilters && (
                  <button
                    type="button"
                    className="clear-filters-btn"
                    onClick={() => {
                      setFilters({ member: '', client: '', project: '', status: '', onTime: '' });
                      setDateRange({ from: '', to: '' });
                    }}
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
            <TaskTable
              tasks={filteredTasks}
              showMemberColumn
              showAdminColumns
              onAdminUpdate={handleAdminUpdate}
              onDeleteTask={handleDeleteTask}
            />
          </div>

          <div className="analytics-section">
            <h2>Performance</h2>
            <div className="charts-grid">
              <div className="chart-card">
                <StatusChart tasks={filteredTasks} />
              </div>
              <div className="chart-card">
                <OnTimeVsDelayedChart tasks={filteredTasks} />
              </div>
              <div className="chart-card">
                <MemberPerformanceChart tasks={filteredTasks} users={users} />
              </div>
              <div className="chart-card">
                <EstimatedVsActualChart tasks={filteredTasks} users={users} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
